import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, TrashIcon } from "@heroicons/react/24/outline";
import { format, isSameDay, isSameMonth, isSameYear, parseISO } from "date-fns";
import { Switch } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ExceptionSlots } from "./exception-slots";
import { AvailabilityBadges } from "../availability-badges";

export const ExceptionsItem = ({ index, isNew = false, onRemove }) => {
    const { control, setValue, getValues, clearErrors, unregister } =
        useFormContext();
    const enabled = useWatch({
        control,
        name: `exceptions.${index}.enabled`,
    });

    const times = useWatch({
        control,
        name: `exceptions.${index}.Times`,
    });

    const dates = useWatch({
        control,
        name: `exceptions.${index}.Dates`,
    });

    const date = dates?.[0];

    const startDateParsed = date?.startDate ? parseISO(date.startDate) : null;
    const endDateParsed = date?.endDate ? parseISO(date.endDate) : null;

    const dateLong = !startDateParsed
        ? "New slot"
        : !endDateParsed || isSameDay(startDateParsed, endDateParsed)
          ? format(startDateParsed, "dd MMM yyyy")
          : isSameYear(startDateParsed, endDateParsed)
            ? isSameMonth(startDateParsed, endDateParsed)
                ? `${format(startDateParsed, "dd")} - ${format(endDateParsed, "dd MMM yyyy")}`
                : `${format(startDateParsed, "dd MMM")} - ${format(endDateParsed, "dd MMM yyyy")}`
            : `${format(startDateParsed, "dd MMM yyyy")} - ${format(
                  endDateParsed,
                  "dd MMM yyyy",
              )}`;

    useEffect(() => {
        if (isNew) {
            setValue(`exceptions.${index}.enabled`, false);

            if (!getValues(`exceptions.${index}`)?.length) {
                setValue(`exceptions.${index}.Times`, [
                    {
                        startTime: "",
                        endTime: "",
                    },
                ]);
                setValue(`exceptions.${index}.Dates`, [
                    {
                        allDay: true,
                    },
                ]);
            }
        }
    }, [index, isNew, setValue, getValues]);

    return (
        <div className="flex gap-2">
            <Disclosure
                as="div"
                defaultOpen={false}
                className="group flex-1 rounded-xl ring ring-gray-300"
            >
                <div className="relative flex min-w-0 flex-wrap pt-3 md:pt-0">
                    <div className="shrink-0 cursor-default ps-3 md:py-3 [&_label]:pl-2 md:[&_label]:pl-3">
                        <Switch
                            name={`exceptions.${index}.enabled`}
                            label={dateLong}
                            labelPlacement="end"
                            size="sm"
                            className="gap-0 text-base font-medium"
                        />
                    </div>
                    <DisclosureButton className="group flex min-w-0 flex-1 cursor-pointer items-center justify-end px-3 md:py-3">
                        <div className="me-8 flex w-full min-w-0 flex-1 flex-wrap justify-end gap-2 overflow-hidden text-right text-sm/5 text-neutral-500 group-data-hover:text-neutral-600 md:me-4 md:me-9">
                            <AvailabilityBadges
                                enabled={enabled}
                                times={times}
                                dates={date}
                                day={`exceptions.${index}`}
                                exception={true}
                            />
                        </div>
                        <ChevronDownIcon className="absolute top-1/2 size-6 shrink-0 -translate-y-1/2 group-data-hover:text-darkgreen-400 group-data-open:rotate-180" />
                    </DisclosureButton>
                    {/* Mobile only */}
                    <DisclosureButton className="flex w-[90%] flex-wrap gap-1 p-1 px-3 pb-2 md:hidden">
                        <AvailabilityBadges
                            enabled={enabled}
                            times={times}
                            dates={date}
                            day={`exceptions.${index}`}
                            mobile={true}
                            exception={true}
                        />
                    </DisclosureButton>
                </div>
                <DisclosurePanel className="grid border-t border-gray-300 p-3">
                    <ExceptionSlots day={`exceptions.${index}`} />
                    {onRemove && (
                        <div className="mt-3 pe-2">
                            <Button
                                type="button"
                                variant="danger"
                                size="sm"
                                onClick={() => {
                                    unregister(`exceptions.${index}`);
                                    clearErrors(`exceptions.${index}`);
                                    onRemove();
                                }}
                            >
                                <TrashIcon />
                                Remove date slot
                            </Button>
                        </div>
                    )}
                </DisclosurePanel>
            </Disclosure>
        </div>
    );
};
